import React from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { useErrorTracking } from '@/hooks/useErrorTracking';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

export function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps) {
  const { captureError } = useErrorTracking();

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    captureError(error, {
      extra: {
        errorInfo,
        context: 'global_boundary',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      },
      tags: {
        component: 'GlobalErrorBoundary',
        level: 'app'
      }
    });
  };

  const GlobalErrorFallback = () => (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-10 w-10 text-destructive" />
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
          <p className="text-muted-foreground">
            We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
          </p>
        </div>
        
        <div className="flex flex-col gap-3">
          <Button 
            onClick={() => window.location.reload()} 
            className="w-full"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/dashboard'}
            className="w-full"
          >
            <Home className="mr-2 h-4 w-4" />
            Go to Dashboard
          </Button>
        </div>
        
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Error ID: {Date.now()}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary
      level="root"
      fallback={<GlobalErrorFallback />}
      onError={handleError}
      enableRetry={false}
      showContactSupport={true}
    >
      {children}
    </ErrorBoundary>
  );
}